import type {AlertaDeReserva} from '@cnc/contracts';
import {Link} from 'react-router';
import {useAlertasDeReservas, useMarcarCambioRevisado} from '../api/reservas';
import {AYUDA_ALERTAS as A} from '../componentes/ayuda-exportaciones';
import {NotaDeAyuda} from '../componentes/nota-de-ayuda';
import {PestanasSeguimiento} from '../componentes/pestanas-seguimiento';
import {usePermisos} from '../componentes/sesion-contexto';
import {tr} from '../i18n/idioma';
import {fecha, numero} from '../utilidades/formato';

// Alertas de las reservas dentro del inventario: 20 y 16 dias antes de la
// salida dicen si todos los contenedores tienen sus Big Bags, y avisan de
// cambios de fecha y de reservas sin exportacion.
export function PaginaAlertasInventario() {
  const permisos = usePermisos();
  const alertas = useAlertasDeReservas();
  const marcar = useMarcarCambioRevisado();

  return (
    <section className="space-y-4">
      <PestanasSeguimiento />
      <div>
        <h1 className="text-2xl font-semibold text-verde-oscuro">{tr('Alertas de reservas', 'Booking alerts')}</h1>
        <p className="text-sm text-apagado">
          {tr(
            'Avisos de las reservas: 20, 16 y 3 días antes de la salida, si todos los contenedores ya tienen sus Big Bags (las que zarpan en 3 días o menos van resaltadas en naranja); cambios de fecha detectados; reservas sin exportación.',
            'Booking alerts: 20, 16 and 3 days before departure, whether all containers already have their Big Bags (those departing within 3 days are highlighted in orange); detected date changes; bookings without an export.',
          )}
        </p>
      </div>

      <div className="flex justify-end">
        <Link to="/seguimiento/reservas" className="text-sm text-verde hover:underline">
          {tr('Ir a reservas →', 'Go to bookings →')}
        </Link>
      </div>

      {alertas.isError && <p className="text-sm text-alerta">{alertas.error.message}</p>}
      {alertas.data && alertas.data.alertas.length === 0 && (
        <p className="rounded-lg border border-linea bg-white p-4 text-sm text-apagado">
          {tr('No hay alertas por ahora.', 'No alerts for now.')}
        </p>
      )}
      <ul className="space-y-2">
        {alertas.data?.alertas.map(a => (
          <Alerta
            key={a.clave}
            alerta={a}
            onRevisada={
              permisos.editar && a.cambioId !== null ? () => void marcar.mutateAsync(a.cambioId as number) : undefined
            }
          />
        ))}
      </ul>
    </section>
  );
}

const TONO: Record<AlertaDeReserva['nivel'], string> = {
  pendiente: 'border-alerta/50 bg-white',
  lista: 'border-verde/40 bg-verde-claro/40',
  informacion: 'border-dorado/60 bg-dorado-claro/40',
};

// Zarpa en 3 dias o menos: color propio, salvo que ya este todo listo.
const TONO_URGENTE = 'border-2 border-urgente bg-urgente-claro';

function Alerta({alerta: a, onRevisada}: {alerta: AlertaDeReserva; onRevisada?: () => void}) {
  const urgente = a.urgente && a.nivel !== 'lista';
  return (
    <li className={`rounded-lg border p-4 text-sm ${urgente ? TONO_URGENTE : TONO[a.nivel]}`}>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="font-medium">
          {a.nivel === 'pendiente' ? '⚠ ' : a.nivel === 'lista' ? '✓ ' : '↻ '}
          {a.titulo}
        </div>
        <div className="flex items-center gap-3 text-xs text-apagado">
          {a.urgente ? (
            <span className="rounded-full bg-urgente px-2 py-0.5 font-semibold text-white">
              {tr('Salida', 'Departure')} {fecha(a.etd)} ·{' '}
              {a.diasParaSalida === 0
                ? tr('zarpa hoy', 'departs today')
                : tr(`zarpa en ${a.diasParaSalida} días`, `departs in ${a.diasParaSalida} days`)}
            </span>
          ) : (
            <span>
              {tr('Salida', 'Departure')} {fecha(a.etd)} (
              {a.diasParaSalida >= 0
                ? tr(`${a.diasParaSalida} días`, `${a.diasParaSalida} days`)
                : tr('ya salió', 'departed')}
              )
            </span>
          )}
          <NotaDeAyuda texto={A.salida} />
          {onRevisada && (
            <button
              type="button"
              onClick={onRevisada}
              className="rounded-md border border-linea bg-white px-2 py-1 hover:bg-papel"
            >
              {tr('✓ Marcar revisado', '✓ Mark as reviewed')}
            </button>
          )}
        </div>
      </div>
      <p className="mt-1">{a.detalle}</p>
      {a.sugerencia && <p className="mt-1 text-apagado">{a.sugerencia}</p>}
      <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-apagado">
        <Link to={`/seguimiento/reservas?id=${a.reservaId}`} className="text-verde hover:underline">
          {a.naviera} {a.reserva}
        </Link>
        {a.exportacion && (
          <Link to={`/seguimiento/${encodeURIComponent(a.exportacion)}`} className="text-verde hover:underline">
            {a.exportacion}
          </Link>
        )}
        <span>
          {tr('Contenedores completos:', 'Full containers:')} {numero(a.contenedoresCompletos)} /{' '}
          {numero(a.contenedores)}
          <NotaDeAyuda texto={A.completos} />
        </span>
        {a.disponiblesDelExportador !== null && (
          <span>
            {tr('Big Bags disponibles del exportador:', "Exporter's available Big Bags:")}{' '}
            {numero(a.disponiblesDelExportador)}
            <NotaDeAyuda texto={A.disponibles} />
          </span>
        )}
      </div>
    </li>
  );
}
